import {
  type Dispatch,
  forwardRef,
  type Ref,
  type SetStateAction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Controller, useForm, type UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { faClipboardList } from '@fortawesome/pro-regular-svg-icons/faClipboardList';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Form,
  Space,
  theme,
  Typography,
} from 'antd';
import isEmpty from 'lodash/isEmpty';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { PosCheckbox } from 'pos-client/components/shared/Form/PosCheckbox.tsx';
import { PosInput } from 'pos-client/components/shared/Form/PosInput.tsx';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import {
  PosDisabledWarning,
  type PosDisabledWarningType,
} from 'pos-client/components/shared/General/PosDisabledWarning.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { useServiceJobTypes } from 'pos-client/hooks/useServiceJobTypes.ts';
import { useSettingsByPath } from 'pos-client/hooks/useSettings.tsx';
import {
  type ModeType,
  useModalType,
} from 'pos-client/pages/locations/Modals/useModalType.tsx';
import {
  type CreateAssetRefinedModel,
  CreateAssetRefinedSchema,
  type CreateRampAssetRefinedModel,
  CreateRampAssetRefinedSchema,
  type UpdateAssetRefinedModel,
  UpdateAssetRefinedSchema,
  type UpdateRampAssetRefinedModel,
  UpdateRampAssetRefinedSchema,
} from 'pos-client/schemas/AssetRefinedSchema.ts';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { sortStrings } from 'pos-client/utils/dataTableHelpers.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import { type AssetModel } from 'pos-shared/models/assetSchema.ts';
import type { IdModel } from 'pos-shared/models/commonSchema.ts';

interface AssetModalProps {
  isVisible: boolean;
  data?: AssetModel | undefined;
  handleOk: (
    data: CreateAssetRefinedModel | UpdateAssetRefinedModel | IdModel,
    mode: ModeType
  ) => void;
  handleCancel: () => void;
  showCapabilities: boolean;
  setShowCapabilities: Dispatch<SetStateAction<boolean>>;
  isCreatingOrUpdating: boolean;
  maxActiveRampsReached: boolean;
  onlyMobileServiceType: boolean;
}

export type AssetModalRefProps = {
  resetForm: UseFormReset<
    | CreateAssetRefinedModel
    | CreateRampAssetRefinedModel
    | UpdateAssetRefinedModel
    | UpdateRampAssetRefinedModel
  >;
};

const MAX_FIELD_LENGTHS = {
  long: 50,
  short: 25,
};

const emptyGuid = '00000000-0000-0000-0000-000000000000';

const daysOfWeekOptions = [
  { label: 'Monday', value: 'Monday' },
  { label: 'Tuesday', value: 'Tuesday' },
  { label: 'Wednesday', value: 'Wednesday' },
  { label: 'Thursday', value: 'Thursday' },
  { label: 'Friday', value: 'Friday' },
  { label: 'Saturday', value: 'Saturday' },
  { label: 'Sunday', value: 'Sunday' },
];

export const AssetModal = forwardRef(function AssetModal(
  props: AssetModalProps,
  ref: Ref<AssetModalRefProps>
) {
  const {
    data,
    isVisible,
    handleOk,
    handleCancel,
    showCapabilities,
    setShowCapabilities,
    isCreatingOrUpdating,
    maxActiveRampsReached,
    onlyMobileServiceType,
  } = props;
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { showError } = useNotifications();
  const { jobTypes, isLoadingJobTypes } = useServiceJobTypes();
  const { first: defaultSetting } = useSettingsByPath({
    path: 'Features>EnableDefaultLanes',
  });

  const defaultEnabled = defaultSetting?.value === 'true';

  const [assetSchema, setAssetSchema] = useState<
    | CreateAssetRefinedModel
    | CreateRampAssetRefinedModel
    | UpdateAssetRefinedModel
    | UpdateRampAssetRefinedModel
    // @ts-ignore
  >(CreateAssetRefinedSchema);

  const modalForm = useForm<
    | CreateAssetRefinedModel
    | CreateRampAssetRefinedModel
    | UpdateAssetRefinedModel
    | UpdateRampAssetRefinedModel
  >({
    shouldFocusError: true,
    mode: 'all',
    resolver: zodResolver(
      // @ts-ignore
      assetSchema as CreateAssetRefinedSchema | CreateRampAssetRefinedSchema
    ),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    resetField,
    watch,
    setValue,
    trigger,
  } = modalForm;

  const { Title } = Typography;
  const { mode, setMode } = useModalType();
  const [canDelete, setCanDelete] = useState<boolean>(true);
  const [countLinkedLocations, setCountLinkedLocations] = useState(0);
  const [enableSubmit, setEnableSubmit] = useState<boolean>(false);
  const isDefault = watch('isDefault');
  const defaultDays = watch('defaultDays');
  const assetTypeName = watch('assetTypeName');

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: () => {
      reset({});
      handleCancel();
    },
  });

  const cancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      reset({});
      handleCancel();
    }
  };

  // queries & mutations
  const { data: assetTypes } = trpc.getAssetTypes.useQuery();

  const { data: linkedAssets } =
    trpc.locations.getLinkedLocationsOfAsset.useQuery({
      id: data?.id || emptyGuid,
    });

  const isRamp = (assetName: string) => assetName.toLowerCase() === 'ramp';

  const skillsOptions = jobTypes?.reduce(
    (acc, { id, name, serviceType }) => {
      return serviceType === 'Static' ? [...acc, { id, name }] : acc;
    },
    [] as { id: string; name: string }[]
  );

  const { showModal: confirmDeleteAsset } = useConfirmModal({
    type: 'delete',
    content: t('assets.deleteAssetConfirm'),
    confirmText: t('assets.deleteAsset'),
    confirmProps: {
      ghost: true,
    },
  });

  const handleRemoveAsset = handleSubmit(() => {
    const formValues = watch();
    const assetId = 'id' in formValues ? formValues.id : undefined;

    if (assetId) {
      confirmDeleteAsset({
        title: `${t('assets.deleteAsset')} ${watch().name}`,
        onConfirm: () => {
          handleOk({ id: assetId }, 'delete');
          reset({});
        },
      });
    } else {
      showError(t('assets.idNotFoundError'));
    }
  });

  const handleAction = handleSubmit((formData) => {
    if (mode === 'edit') {
      handleOk(
        {
          id: data?.id || '',
          ...formData,
          defaultDays: isDefault ? formData.defaultDays : [],
        },
        'edit'
      );
    } else {
      handleOk(
        {
          ...formData,
          isActive: true,
        },
        'create'
      );
    }
  });

  const handleSetAssetSchema = (isRampType: boolean) => {
    if (isRampType) {
      if (mode === 'edit') {
        setAssetSchema(
          UpdateRampAssetRefinedSchema as unknown as UpdateRampAssetRefinedModel
        );
      } else {
        setAssetSchema(
          CreateRampAssetRefinedSchema as unknown as CreateRampAssetRefinedModel
        );
      }
    } else {
      if (mode === 'edit') {
        setAssetSchema(
          UpdateAssetRefinedSchema as unknown as UpdateAssetRefinedModel
        );
      } else {
        setAssetSchema(
          CreateAssetRefinedSchema as unknown as CreateAssetRefinedModel
        );
      }
    }
  };

  const handleTypeChange = (val: string) => {
    const assetName = assetTypes?.find((asset) => asset.id === val)?.name;
    const isValRamp = assetName ? isRamp(assetName) : false;

    if (isValRamp) {
      setShowCapabilities(true);
    } else {
      // Need to reset the skills if we're not in a ramp
      resetField('skills', undefined);
      setShowCapabilities(false);
    }
    handleSetAssetSchema(isValRamp);
    setValue('assetTypeName', assetName);
    trigger('name');
  };

  useEffect(() => {
    const isRampType: boolean = data?.assetTypeName
      ? isRamp(data.assetTypeName)
      : false;

    if (data && !isEmpty(data)) {
      reset(data);
      setMode('edit');
      if (isRampType) {
        setShowCapabilities(true);

        if (data.defaultDays && data.defaultDays.length > 0) {
          setValue('isDefault', true);
        }
      } else {
        setShowCapabilities(false);
      }
      handleSetAssetSchema(isRampType);
    } else {
      reset({});
      setMode('create');
    }
  }, [isVisible]);

  useEffect(() => {
    let countLinkedLocation = 0;
    let hasLinkedLocation;
    const linkedLocationsData = linkedAssets?.results;
    linkedLocationsData?.forEach((linkedLocation) => {
      linkedLocation?.linkedAssets?.forEach((asset) => {
        if (asset?.id === data?.id) {
          countLinkedLocation++;
          hasLinkedLocation = true;
        }
      });
    });
    const hasLinks =
      linkedAssets &&
      linkedAssets.results &&
      linkedAssets.results.length > 0 &&
      hasLinkedLocation;
    if (hasLinks) {
      setCanDelete(false);
      setCountLinkedLocations(countLinkedLocation);
    } else {
      setCanDelete(true);
    }
  }, [linkedAssets]);

  useEffect(() => {
    const selectedValue = watch((value) => {
      setEnableSubmit(!!value?.reference && !!value?.name);
    });

    return () => selectedValue.unsubscribe();
  }, [watch]);

  useEffect(() => {
    const skills = watch('skills');
    const isDefault = watch('isDefault');
    const defaultDays = watch('defaultDays');

    if (showCapabilities && (!skills || skills.length === 0)) {
      setEnableSubmit(false);
    } else if (isDefault && (!defaultDays || defaultDays.length === 0)) {
      setEnableSubmit(false);
    } else if (!isDefault && defaultDays) {
      resetField('defaultDays');
    } else {
      setEnableSubmit(true);
    }
  }, [showCapabilities, isDefault, defaultDays]);

  useImperativeHandle(ref, () => {
    return {
      resetForm: reset,
    };
  }, []);

  const addActivateRampDisabledWarning = useMemo<PosDisabledWarningType>(() => {
    return {
      condition:
        maxActiveRampsReached &&
        assetTypeName === 'Ramp' &&
        data?.assetTypeName !== 'Ramp' &&
        !onlyMobileServiceType,
      key: 'maxRamps',
      text: t('assets.max20Ramps'),
    };
  }, [
    maxActiveRampsReached,
    onlyMobileServiceType,
    assetTypeName,
    data?.assetTypeName,
    t,
  ]);

  return (
    <PosModal
      header={{
        title:
          mode === 'edit' ? t('assets.editAsset') : t('assets.createAsset'),
        icon: faClipboardList,
      }}
      open={isVisible}
      onCancel={cancelModal}
      footer={
        <Flex justify="space-between" style={fullWidth}>
          <Button
            danger
            onClick={handleRemoveAsset}
            loading={isCreatingOrUpdating}
            hidden={mode !== 'edit'}
          >
            {t('common.buttons.delete')}
          </Button>
          <Space style={{ flexGrow: 1, justifyContent: 'flex-end' }}>
            <Button
              type="default"
              onClick={cancelModal}
              style={{ marginLeft: 'auto' }}
            >
              {t('common.buttons.cancel')}
            </Button>

            <PosDisabledWarning warnings={addActivateRampDisabledWarning}>
              <Button
                key="submit"
                type="primary"
                onClick={handleAction}
                loading={isCreatingOrUpdating}
                disabled={
                  !isValid ||
                  !isDirty ||
                  !enableSubmit ||
                  addActivateRampDisabledWarning.condition
                }
              >
                {mode === 'edit'
                  ? t('common.buttons.save')
                  : t('common.buttons.create')}
              </Button>
            </PosDisabledWarning>
          </Space>
        </Flex>
      }
    >
      {mode === 'edit' && <Title level={5}>{watch()?.name}</Title>}
      <Form layout="vertical">
        <PosInput
          control={control}
          name="name"
          maxLength={MAX_FIELD_LENGTHS.long}
          label={t('assets.assetName')}
        />
        <PosInput
          control={control}
          name="reference"
          label={t('assets.assetReference')}
          maxLength={MAX_FIELD_LENGTHS.long}
          disabled={mode === 'edit' ? true : false}
        />
        <PosInput
          control={control}
          name="description"
          label={t('common.words.description')}
          maxLength={MAX_FIELD_LENGTHS.long}
        />
        <PosSelect
          control={control}
          name="typeId"
          label={t('assets.assetType')}
          options={assetTypes?.filter(
            (x) => x.name.toLowerCase() !== 'technician'
          )}
          filterSort={(optionA, optionB) =>
            sortStrings(optionA, optionB, 'name')
          }
          showSearch
          optionFilterProp="name"
          fieldNames={{
            label: 'name',
            value: 'id',
          }}
          onSelect={handleTypeChange}
          filterOption={(input, option) =>
            (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
        {showCapabilities ? (
          <>
            <PosSelect
              control={control}
              name="skills"
              label={t('locations.capabilities')}
              options={skillsOptions}
              showSearch
              mode="multiple"
              optionFilterProp="name"
              loading={isLoadingJobTypes}
              fieldNames={{
                label: 'name',
                value: 'name',
              }}
              filterOption={(input, option) =>
                (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
            {defaultEnabled && (
              <Form.Item label={t('locations.defaultAsset')}>
                <Typography.Text
                  style={{
                    color: token.colorTextSecondary,
                    marginBottom: token.marginSM,
                    display: 'block',
                  }}
                >
                  {t('locations.defaultDesc')}
                </Typography.Text>
                <PosCheckbox control={control} name="isDefault" noStyle>
                  {t('locations.defaultAsset')}
                </PosCheckbox>
              </Form.Item>
            )}
            {defaultEnabled && isDefault && (
              <Controller
                control={control}
                name="defaultDays"
                render={() => (
                  <>
                    <Controller
                      control={control}
                      name="defaultDays"
                      render={({ field: { onChange, name, value } }) => (
                        <Checkbox.Group
                          name={name}
                          options={daysOfWeekOptions}
                          defaultValue={value}
                          onChange={onChange}
                          style={{
                            display: 'grid',
                            gap: token.margin,
                            gridTemplateColumns: '1fr 1fr 1fr 1fr',
                            marginBottom: token.marginMD,
                          }}
                        />
                      )}
                    />
                  </>
                )}
              />
            )}
          </>
        ) : null}
        {!canDelete ? (
          <Alert
            type="error"
            message={t('assets.assetLinkedToServiceLocation', {
              count: countLinkedLocations,
            })}
            style={{ marginTop: token.marginMD }}
          />
        ) : null}
      </Form>
    </PosModal>
  );
});
